(function MediaHubDragDrop($, Adzuki)
{
	"use strict";

	const MAX_FILE_SIZE         = 200 * 1024 * 1024;
	const FILE_TYPE_NOT_ALLOWED = 'File type is not allowed.';

	const mediaHubDragDropApp = {
		props: {
			allowMediaSelect: {
				type   : Boolean,
				default: false
			},
			bindKey: {
				type   : String,
				default: ''
			},
			elementMax: {
				type   : Number,
				default: 0
			},
			inputData: {
				type   : Object,
				default: function defaultInputData() {
					return {};
				}
			},
			mediaSelectMode: {
				type   : String,
				default: 'inline'
			},
			hostElement: {
				type   : Object,
				default: null
			},
			section: {
				type   : String,
				default: ''
			},
			targetId: {
				type   : String,
				default: ''
			}
		},
		data() {
			return {
				allowedExtensions   : [],
				bootstrapError      : false,
				bootstrapErrorDetail: '',
				dragActive          : false,
				effectiveMax        : 1,
				form                : null,
				items               : [],
				pickerAvailable     : false,
				pickerOpen          : false,
				queueRunning        : false,
				ready               : false,
				refreshTimer        : null,
				sectionInfo         : {},
				sectionUnsupported  : false,
				tosAgreed           : false,
				tosChecked          : false
			};
		},
		computed: {
			acceptTypes() {
				return this.allowedExtensions.map(extension => '.' + extension).join(',');
			},
			canSelectFiles() {
				return this.ready
					&& this.tosAgreed
					&& !this.hasPendingUploads()
					&& (this.effectiveMax === 1 || !this.isFull);
			},
			canSelectMedia() {
				return this.pickerAvailable
					&& this.ready
					&& this.tosAgreed
					&& !this.hasPendingUploads()
					&& (this.effectiveMax === 1 || !this.isFull);
			},
			configurationMessage() {
				if (this.sectionUnsupported) {
					return [
						'This Media Hub section does not support inline uploads.',
						'Sections with resize dimensions or needs_approval cannot use MediaHubDragDropUpload.',
					].join(' ');
				}

				const parts = [
					'Inline Media Hub upload is unavailable.',
				];

				if (this.bootstrapErrorDetail) {
					parts.push(this.bootstrapErrorDetail);
				}

				parts.push([
					'Developer check: confirm the section exists in media_sections,',
					'Media/getAllowedExtensions returns a non-empty array for this section,',
					'and the mapped model exposes a public $allowed_files property',
					'(plural section names like "group-attachments" need an explicit case in MediaController::getAllowedExtensions).',
				].join(' '));

				return parts.join(' ');
			},
			displayDropText() {
				if (this.isFull) {
					return 'Attachment limit reached,';
				}

				if (this.effectiveMax === 1 && this.attachedCount > 0) {
					return 'Drop a file here to replace the current one,';
				}

				if (this.inputData.dropText) {
					return this.inputData.dropText;
				}

				if (this.effectiveMax > 1) {
					return `Drop up to ${this.effectiveMax} files here to attach,`;
				}

				return 'Drop file here to attach,';
			},
			mediaHubLink() {
				return String(Adzuki.mediahub_url || '') + String(Adzuki.base_uri || '') + '/Media';
			},
			pickerIframeHtml() {
				const iframeSrc = String(Adzuki.mediahub_url || '')
					+ String(Adzuki.base_uri || '')
					+ '/Media/cpMediaHub'
					+ '/section:' + encodeURIComponent(this.section)
					+ '/tab:select'
					+ '/targetId:' + encodeURIComponent(this.targetId)
					+ '/hide_upload:1'
					+ '/hide_select_all:1'
					+ '/selection_limit:' + this.pickerSelectionLimit;

				return '<iframe'
					+ ' src="' + iframeSrc + '"'
					+ ' width="100%"'
					+ ' height="100%"'
					+ ' style="height:100%; border:none; display:block;"'
					+ ' allowfullscreen'
					+ '></iframe>';
			},
			attachedCount() {
				return this.items.length;
			},
			isFull() {
				return this.effectiveMax > 1 && this.attachedCount >= this.effectiveMax;
			},
			pickerSelectionLimit() {
				if (this.effectiveMax === 1) {
					return 1;
				}

				return this.remainingSlots;
			},
			remainingSlots() {
				return Math.max(0, this.effectiveMax - this.attachedCount);
			},
			selectButtonText() {
				return this.effectiveMax === 1 && this.attachedCount > 0
					? 'Replace from Maxx Media'
					: 'Select from Maxx Media';
			},
			slotCounterText() {
				const count = `${this.attachedCount} of ${this.effectiveMax} attached`;

				if (this.effectiveMax === 1 && this.attachedCount > 0) {
					return count + ' · a new file replaces it';
				}

				return count;
			}
		},
		provide() {
			return {
				formatDate        : this.formatDate,
				formatDuration    : this.formatDuration,
				getMediaSrcString : this.getMediaSrcString,
				formatFileSize    : this.formatFileSize,
				fileIcon          : this.fileIcon,
				openToast         : this.openToast,
				openAlert         : this.openAlert,
				getSectionInfo    : this.getSectionInfo,
				normalizeExtension: this.normalizeExtension,
				sendMediaToParent : this.sendMediaToParent,
				statusAction      : this.statusAction,
				fileNameFromPath  : this.fileNameFromPath,
				mediaAssetSrc     : this.mediaAssetSrc
			};
		},
		methods: {
			async bootstrap() {
				if (!this.section) {
					this.failBootstrap('MediaHubDragDropUpload requires a Media Hub section.');
					return;
				}

				try {
					this.sectionInfo = await this.fetchJson(
						`${Adzuki.base_uri}/MediaSection/getSection/${encodeURIComponent(this.section)}`
					);

					if (!this.sectionInfo || this.sectionInfo.error) {
						throw new Error(this.sectionInfo && this.sectionInfo.error
							? this.sectionInfo.error
							: `Media section "${this.section}" was not found in media_sections.`);
					}

					if (
						Number(this.sectionInfo.resize_width) > 0 ||
						Number(this.sectionInfo.resize_height) > 0 ||
						Number(this.sectionInfo.needs_approval) === 1
					) {
						this.sectionUnsupported = true;
						console.error(
							'MediaHubDragDropUpload does not support sections that resize files or require approval.',
							this.sectionInfo
						);
						return;
					}

					this.allowedExtensions = await this.fetchJson(
						`${Adzuki.base_uri}/Media/getAllowedExtensions/section:${encodeURIComponent(this.section)}`
					);

					if (!Array.isArray(this.allowedExtensions) || this.allowedExtensions.length === 0) {
						throw new Error(
							`No allowed file extensions were returned for section "${this.section}". `
							+ 'Check MediaController::getAllowedExtensions and that the mapped model has a public $allowed_files property.'
						);
					}

					this.allowedExtensions = this.allowedExtensions.map(extension => {
						return String(extension).replace(/^\./, '').toLowerCase();
					});

					await this.refreshTos();
					this.resolveEffectiveMax();
					this.ready = true;
					this.processQueue();
				} catch (error) {
					this.failBootstrap('Unable to initialize inline Media Hub upload.', error);
				}
			},
			closePicker() {
				this.pickerOpen = false;
			},
			dispatchInputChange(input) {
				if (input) {
					input.dispatchEvent(new Event('change', { bubbles: true }));
				}
			},
			failBootstrap(message, error) {
				this.bootstrapError = true;
				this.bootstrapErrorDetail = error && error.message
					? String(error.message)
					: String(message || '');
				console.error(message, error || '');
			},
			async fetchJson(url, options) {
				const response = await fetch(url, options);
				let data;

				if (!response.ok) {
					throw new Error(`${response.status} ${response.statusText}`);
				}

				data = await response.json();
				return data;
			},
			async fetchMediaItems(mediaIds) {
				const formData = new FormData();

				formData.append('media_ids', JSON.stringify(mediaIds));

				return this.fetchJson(`${Adzuki.base_uri}/Media/getMediaItemsById`, {
					method: 'POST',
					body  : formData
				});
			},
			fileIcon(fileType) {
				const extension = String(fileType || '').toLowerCase();
				const icons = {
					csv : 'far fa-file-excel',
					doc : 'far fa-file-word',
					docx: 'far fa-file-word',
					gif : 'far fa-file-image',
					jpeg: 'far fa-file-image',
					jpg : 'far fa-file-image',
					mov : 'far fa-file-video',
					mp4 : 'far fa-file-video',
					pdf : 'far fa-file-pdf',
					png : 'far fa-file-image',
					ppt : 'far fa-file-powerpoint',
					pptx: 'far fa-file-powerpoint',
					txt : 'far fa-file-alt',
					xls : 'far fa-file-excel',
					xlsx: 'far fa-file-excel'
				};

				return icons[extension] || 'far fa-file';
			},
			fileNameFromPath(path) {
				return String(path || '').replace(/\\/g, '/').split('/').pop() || '';
			},
			formatDate(date) {
				if (!date) {
					return '';
				}

				return new Date(String(date).replace(' ', 'T')).toLocaleDateString();
			},
			formatDuration(seconds) {
				const minutes = Math.floor(Number(seconds) / 60);
				const remainder = Math.floor(Number(seconds) % 60);

				return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
			},
			formatFileSize(bytes) {
				const units = ['B', 'KB', 'MB', 'GB'];
				let size    = Number(bytes) || 0;
				let unit    = 0;

				while (size >= 1024 && unit < units.length - 1) {
					size /= 1024;
					unit++;
				}

				return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
			},
			getMediaSrcString(item) {
				if (item && item.remote_url) {
					return item.remote_url;
				}

				return this.mediaAssetSrc(
					String(item && item.path ? item.path : '')
					+ String(item && (item.actual_file_name || item.original_file_name)
						? item.actual_file_name || item.original_file_name
						: '')
				);
			},
			getSectionInfo() {
				return this.sectionInfo;
			},
			getExtension(file) {
				const parts = String(file && file.name ? file.name : '').split('.');

				if (parts.length < 2) {
					return '';
				}

				return parts.pop().toLowerCase();
			},
			getMediaExtension(media) {
				const fromField = String(media && media.extension ? media.extension : '')
					.replace(/^\./, '')
					.toLowerCase();

				if (fromField) {
					return fromField;
				}

				return this.getExtension({
					name: media && (media.original_file_name || media.actual_file_name || media.title)
				});
			},
			handleDrop(event) {
				this.dragActive = false;

				if (!this.canSelectFiles) {
					return;
				}

				this.handleFiles(Array.from(event.dataTransfer.files || []));
			},
			handleFileSelect(event) {
				const files = Array.from(event.target.files || []);

				event.target.value = '';
				this.handleFiles(files);
			},
			handleFiles(files) {
				const selectedFiles  = Array.from(files || []);
				let availableSlots   = this.remainingSlots;

				if (!this.canSelectFiles || selectedFiles.length === 0) {
					return;
				}

				if (this.effectiveMax === 1) {
					this.items = [];
					availableSlots = 1;
				}

				if (selectedFiles.length > availableSlots) {
					this.notifyError(
						`You cannot attach more than ${this.effectiveMax} file${this.effectiveMax === 1 ? '' : 's'}.`
					);
				}

				selectedFiles.slice(0, availableSlots).forEach(file => {
					const validationError = this.validateFile(file);

					this.items.push({
						error    : validationError,
						file     : file,
						key      : `mh-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
						mediaId  : null,
						name     : file.name,
						origin   : 'uploaded',
						removable: true,
						size     : file.size,
						status   : validationError ? 'error' : 'queued'
					});
				});

				this.writeBack();
				this.processQueue();
			},
			async handleSelectedMedia(data) {
				const selectedIds = [].concat(data && data.media_id ? data.media_id : [])
					.map(mediaId => String(mediaId))
					.filter(mediaId => Boolean(mediaId));
				let acceptedIds;
				let mediaItems = [];

				if (selectedIds.length === 0) {
					return;
				}

				if (this.effectiveMax === 1) {
					acceptedIds = selectedIds.slice(-1);
					this.items = [];
				} else {
					acceptedIds = selectedIds
						.filter(mediaId => {
							return !this.items.some(item => String(item.mediaId) === mediaId);
						})
						.slice(0, this.remainingSlots);
				}

				if (acceptedIds.length === 0) {
					this.closePicker();
					return;
				}

				if (acceptedIds.length < selectedIds.length && this.effectiveMax > 1) {
					this.notifyError(`You cannot attach more than ${this.effectiveMax} files.`);
				}

				try {
					mediaItems = await this.fetchMediaItems(acceptedIds);
				} catch (error) {
					console.error('Unable to load selected Media Hub file details.', error);
				}

				acceptedIds.forEach(mediaId => {
					const media = Array.isArray(mediaItems)
						? mediaItems.find(item => String(item.id) === mediaId)
						: null;
					const resolved = media || { id: mediaId };
					const validationError = this.validateExtension(this.getMediaExtension(resolved));
					const name = resolved.title ||
						resolved.original_file_name ||
						resolved.actual_file_name ||
						`Media #${mediaId}`;

					if (validationError) {
						this.items.push({
							error    : validationError,
							file     : null,
							key      : `mh-selected-${mediaId}-${Math.random().toString(36).slice(2)}`,
							mediaId  : null,
							name     : name,
							origin   : 'selected',
							removable: true,
							size     : Number(resolved.size || resolved.file_size) || 0,
							status   : 'error'
						});
						return;
					}

					this.items.push(this.mediaItemToRow(resolved, 'selected', true));
				});

				this.writeBack();
				this.closePicker();
			},
			handleIframeMessage(event) {
				let mediaHubOrigin;

				if (this.mediaSelectMode !== 'iframe') {
					return;
				}

				try {
					mediaHubOrigin = new URL(Adzuki.mediahub_url, window.location.href).origin;
				} catch (error) {
					console.error('MediaHubDragDropUpload could not validate the Media Hub origin.', error);
					return;
				}

				if (event.origin !== mediaHubOrigin) {
					return;
				}

				const eventData = event.data || {};

				if (eventData.targetId !== this.targetId) {
					return;
				}

				if (eventData.type === 'MEDIA_SELECTED') {
					this.handleSelectedMedia(eventData.payload);
				} else if (eventData.type === 'CLOSE_MODAL') {
					this.closePicker();
				}
			},
			handleSubmit(event) {
				if (!this.hasPendingUploads()) {
					return;
				}

				event.preventDefault();
				event.stopImmediatePropagation();
				this.notifyError('Please wait for Media Hub uploads to finish before submitting.');
			},
			hasPendingUploads() {
				return this.items.some(item => {
					return item.status === 'queued' || item.status === 'uploading';
				});
			},
			async hydrateExistingItems(mediaIds) {
				const existingIds = mediaIds.map(mediaId => String(mediaId));

				this.items = existingIds.map(mediaId => {
					return this.mediaItemToRow({ id: mediaId }, 'existing', false);
				});

				try {
					const mediaItems = await this.fetchMediaItems(existingIds);

					if (!Array.isArray(mediaItems)) {
						throw new Error('Media item hydration returned an invalid response.');
					}

					this.items = existingIds.map(mediaId => {
						const media = mediaItems.find(item => String(item.id) === mediaId);

						return media
							? this.mediaItemToRow(media, 'existing', true)
							: this.mediaItemToRow({ id: mediaId }, 'existing', false);
					});
				} catch (error) {
					console.error('Unable to hydrate existing Media Hub attachments.', error);
				}
			},
			isDisallowedFileType(item) {
				return Boolean(item && item.error === FILE_TYPE_NOT_ALLOWED);
			},
			mediaAssetSrc(value) {
				if (!value || (/^https?:\/\//i).test(value)) {
					return value || '';
				}

				return `${Adzuki.base_uri}/${String(value).replace(/^\//, '')}`;
			},
			mediaItemToRow(media, origin, removable) {
				const mediaId = String(media.id);
				const name = media.title ||
					media.original_file_name ||
					media.actual_file_name ||
					`Media #${mediaId}`;

				return {
					error    : '',
					file     : null,
					key      : `mh-${origin}-${mediaId}-${Math.random().toString(36).slice(2)}`,
					mediaId  : mediaId,
					name     : name,
					origin   : origin,
					removable: removable,
					size     : Number(media.size || media.file_size) || 0,
					status   : 'saved'
				};
			},
			normalizeExtension(extension) {
				const normalized = String(extension || '').toLowerCase();

				if (normalized === 'jpeg') {
					return 'jpg';
				}
				if (['docx'].includes(normalized)) {
					return 'doc';
				}
				if (['csv', 'xlsx'].includes(normalized)) {
					return 'xls';
				}
				if (normalized === 'pptx') {
					return 'ppt';
				}

				return normalized;
			},
			notifyError(message) {
				if (typeof toastr !== 'undefined' && toastr.error) {
					toastr.error(message);
					return;
				}

				console.error(message);
			},
			openAlert(type, title, message) {
				this.notifyError(message || title || type);
			},
			openPicker() {
				if (this.canSelectMedia) {
					this.pickerOpen = true;
				}
			},
			openToast(status, message) {
				if (status === 'error') {
					this.notifyError(message);
				}
			},
			processQueue() {
				if (this.queueRunning || !this.ready || !this.tosAgreed) {
					return;
				}

				this.queueRunning = true;

				this.runQueue().finally(() => {
					this.queueRunning = false;

					if (this.items.some(item => item.status === 'queued')) {
						this.processQueue();
					}
				});
			},
			async refreshTos() {
				const data = await this.fetchJson(`${Adzuki.base_uri}/Media/checkTos`);

				this.tosAgreed  = Number(data && data.agreed) === 1;
				this.tosChecked = true;

				if (this.tosAgreed) {
					this.processQueue();
				}
			},
			removeItem(index) {
				if (!this.items[index] || this.items[index].status === 'uploading') {
					return;
				}

				this.items.splice(index, 1);
				this.writeBack();
			},
			reset() {
				this.items = [];
				this.closePicker();
				this.writeBack();
			},
			resolveEffectiveMax() {
				const sectionMax = Number(this.sectionInfo.upload_limit);
				const elementMax = Number(this.elementMax);
				const limits     = [];

				if (sectionMax > 0) {
					limits.push(sectionMax);
				}

				if (elementMax > 0) {
					limits.push(elementMax);
				}

				this.effectiveMax = limits.length > 0 ? Math.min(...limits) : 1;
			},
			resolveMediaInput() {
				const wrapper = this.hostElement ? this.hostElement.closest('.mh-dragdrop-wrapper') : null;
				const scope   = this.hostElement ? this.hostElement.closest('form') || wrapper : wrapper;
				let input;

				if (this.bindKey && scope) {
					input = Array.from(scope.querySelectorAll('input[data-bind-key]')).find(bindMatch => {
						return bindMatch.getAttribute('data-bind-key') === this.bindKey;
					});
					if (input) {
						return input;
					}
				}

				if (!wrapper) {
					return null;
				}

				return wrapper.querySelector('.mh-dragdrop-media-ids');
			},
			retryItem(index) {
				const item = this.items[index];
				const validationError = item ? this.validateFile(item.file) : 'File is unavailable.';

				if (!item) {
					return;
				}

				item.error = validationError;
				item.status = validationError ? 'error' : 'queued';
				this.processQueue();
			},
			async runQueue() {
				let item;

				while (this.tosAgreed) {
					item = this.items.find(queueItem => queueItem.status === 'queued');

					if (!item) {
						return;
					}

					await this.uploadItem(item);
				}
			},
			scheduleTosRefresh() {
				if (document.visibilityState === 'hidden') {
					return;
				}

				window.clearTimeout(this.refreshTimer);
				this.refreshTimer = window.setTimeout(() => {
					this.refreshTos().catch(error => {
						console.error('Unable to refresh Media Hub Terms of Service status.', error);
					});
				}, 250);
			},
			sendMediaToParent(mediaData) {
				this.handleSelectedMedia(mediaData);
			},
			statusAction(item) {
				return Number(this.sectionInfo.needs_approval) === 1 && item.status === 'pending';
			},
			triggerFileInput() {
				if (this.canSelectFiles) {
					this.$refs.fileInput.click();
				}
			},
			async uploadItem(item) {
				const formData = new FormData();
				let data;
				let mediaId;
				let selectData;
				let selectResponse;

				item.error  = '';
				item.status = 'uploading';
				formData.append('file', item.file);
				formData.append('section', this.section);

				try {
					data = await this.fetchJson(`${Adzuki.base_uri}/Media/saveMediaHubFile/`, {
						method: 'POST',
						body  : formData
					});

					if (data.status === 'pending') {
						throw new Error('This upload unexpectedly requires approval and cannot be attached here.');
					}

					if (data.status === 'existing_asset') {
						mediaId = data.existing_asset
							&& data.existing_asset.media
							&& data.existing_asset.media.id;
					} else if (data.status === 'success') {
						mediaId = data.media_id;
					} else {
						throw new Error(data.message || data.too_many_files || 'Upload failed.');
					}

					if (mediaId === undefined || mediaId === null || String(mediaId) === '') {
						throw new Error('The Media Hub response did not include a media id.');
					}

					if (data.status === 'existing_asset') {
						selectData = new FormData();
						selectData.append('id', mediaId);
						selectData.append('section', this.section);

						selectResponse = await this.fetchJson(`${Adzuki.base_uri}/Media/selectMediaFile/`, {
							method: 'POST',
							body  : selectData
						});

						if (selectResponse.status === 'pending') {
							throw new Error('This existing file unexpectedly requires approval and cannot be attached here.');
						}

						if (selectResponse.status !== 'success') {
							throw new Error(selectResponse.message || 'Unable to attach the existing Media Hub file.');
						}
					}

					item.mediaId = String(mediaId);
					item.status  = 'saved';
					this.writeBack();
				} catch (error) {
					item.error  = error.message || 'Upload failed.';
					item.status = 'error';
					console.error(`Unable to upload ${item.file.name}:`, error);
				}
			},
			validateExtension(extension) {
				const normalized = String(extension || '').replace(/^\./, '').toLowerCase();

				if (!normalized || !this.allowedExtensions.includes(normalized)) {
					return FILE_TYPE_NOT_ALLOWED;
				}

				return '';
			},
			validateFile(file) {
				if (Number(file.size) > MAX_FILE_SIZE) {
					return 'File exceeds 200MB.';
				}

				return this.validateExtension(this.getExtension(file));
			},
			writeBack() {
				const input    = this.resolveMediaInput();
				const savedIds = this.items
					.filter(item => item.status === 'saved' && item.mediaId)
					.map(item => item.mediaId);

				if (!input) {
					console.error('MediaHubDragDropUpload could not find its media id input.');
					return;
				}

				input.value = savedIds.join(',');
				this.dispatchInputChange(input);
			}
		},
		async mounted() {
			const input = this.resolveMediaInput();
			const existingMediaIds = input
				? String(input.value || '')
					.split(',')
					.map(value => value.trim())
					.filter(value => Boolean(value))
				: [];

			if (!input) {
				this.failBootstrap('MediaHubDragDropUpload requires a media id input.');
				return;
			}

			this.pickerAvailable = this.allowMediaSelect
				&& (
					this.mediaSelectMode === 'iframe' ||
					Boolean(document.querySelector('#adzuki-select-files'))
				);

			if (existingMediaIds.length > 0) {
				await this.hydrateExistingItems(existingMediaIds);
			}

			this.form = this.hostElement ? this.hostElement.closest('form') : null;
			if (this.form) {
				this.form.addEventListener('submit', this.handleSubmit, true);
			}

			window.addEventListener('focus', this.scheduleTosRefresh);
			window.addEventListener('message', this.handleIframeMessage);
			document.addEventListener('visibilitychange', this.scheduleTosRefresh);
			this.bootstrap();
		},
		beforeUnmount() {
			window.clearTimeout(this.refreshTimer);
			window.removeEventListener('focus', this.scheduleTosRefresh);
			window.removeEventListener('message', this.handleIframeMessage);
			document.removeEventListener('visibilitychange', this.scheduleTosRefresh);

			if (this.form) {
				this.form.removeEventListener('submit', this.handleSubmit, true);
			}
		}
	};

	Adzuki.MediaHubDragDrop = {
		init: function init(container) {
			const $container = $(container);
			const inputData  = $container.attr('data-input-data');
			const allowMediaSelect = $container.attr('data-allow-media-select') === '1';
			const mediaSelectMode = String($container.attr('data-media-select-mode') || 'inline');
			const inlinePickerAvailable = Boolean(document.querySelector('#adzuki-select-files'));
			const pickerAvailable = mediaSelectMode === 'iframe' || inlinePickerAvailable;
			const targetId = 'mh_dragdrop_' + Math.random().toString(36).slice(2) + Date.now();
			let parsedInputData;
			let app;
			let mountedApp;

			if ($container.data('vue-initialized')) {
				console.warn('MediaHub drag/drop upload is already initialized on this element.');
				return;
			}

			try {
				parsedInputData = inputData ? JSON.parse(atob(inputData)) : {};
			} catch (error) {
				parsedInputData = {};
				console.error('Unable to parse MediaHub drag/drop input data.', error);
			}

			if (allowMediaSelect && mediaSelectMode !== 'iframe' && !inlinePickerAvailable) {
				console.error(
					'MediaHubDragDropUpload cannot enable Maxx Media selection because #adzuki-select-files is missing. '
					+ 'Render MediaHubComponents once in the hosting view.'
				);
			}

			app = Vue.createApp(mediaHubDragDropApp, {
				allowMediaSelect: allowMediaSelect && pickerAvailable,
				bindKey         : String($container.attr('data-bind-key') || ''),
				elementMax      : Number($container.attr('data-max-files')) || 0,
				hostElement     : container,
				inputData       : parsedInputData,
				mediaSelectMode : mediaSelectMode,
				section         : String($container.attr('data-section') || ''),
				targetId        : targetId
			});

			app.config.globalProperties.$baseUri       = Adzuki.base_uri;
			app.config.globalProperties.$agentId       = Adzuki.agent_id;
			app.config.globalProperties.$franchiseId   = Adzuki.franchise_id;
			app.config.globalProperties.$section       = String($container.attr('data-section') || '');
			app.config.globalProperties.$tab           = 'select';
			app.config.globalProperties.$targetId      = targetId;
			app.config.globalProperties.$needsApproval = null;

			if (allowMediaSelect) {
				app.component('adzuki-modal', Adzuki.VueComponents.AdzukiModal);
				app.component('adzuki-media-hub', Adzuki.VueComponents.AdzukiMediaHub);
				// the picker shows the Media Hub terms gate first (MEDIA-288)
				app.component('adzuki-tos', Adzuki.VueComponents.AdzukiTos);
			}

			if (allowMediaSelect && mediaSelectMode !== 'iframe' && inlinePickerAvailable) {
				app.component('adzuki-alert', Adzuki.VueComponents.AdzukiAlert);
				app.component('adzuki-confirm', Adzuki.VueComponents.AdzukiConfirm);
				app.component('adzuki-existing-file', Adzuki.VueComponents.AdzukiExistingFile);
				app.component('adzuki-file-upload', Adzuki.VueComponents.AdzukiFileUpload);
				app.component('adzuki-file-upload-form', Adzuki.VueComponents.AdzukiFileUploadForm);
				app.component('adzuki-loading', Adzuki.VueComponents.AdzukiLoading);
				app.component('adzuki-multiselect', Adzuki.VueComponents.AdzukiMultiselect);
				app.component('adzuki-pexels', Adzuki.VueComponents.AdzukiPexels);
				app.component('adzuki-pop-form', Adzuki.VueComponents.AdzukiProofOfPurchase);
				app.component('adzuki-select-files', Adzuki.VueComponents.AdzukiSelectFiles);
				app.component('adzuki-sort', Adzuki.VueComponents.AdzukiSort);
				app.component('adzuki-video-recorder', Adzuki.VueComponents.AdzukiVideoRecorder);
				app.component('adzuki-vue-image-cropper', Adzuki.VueComponents.AdzukiVueImageCropper);
				app.component('mediahub-details-component', Adzuki.VueComponents.MediaHubDetails);
				app.component('mediahub-search-filters', Adzuki.VueComponents.MediaHubSearchFilter);

				if (Adzuki.VueDirectives && Adzuki.VueDirectives.Tooltip) {
					app.directive('tooltip', Adzuki.VueDirectives.Tooltip);
				}
			}

			mountedApp = app.mount(container);
			$container.data('vue-initialized', true);
			$container.data('vue-app-instance', mountedApp);
			return mountedApp;
		},
		hasPendingUploads: function hasPendingUploads(container) {
			const instance = $(container).data('vue-app-instance');

			return Boolean(instance && instance.hasPendingUploads());
		},
		reset: function reset(container) {
			const instance = $(container).data('vue-app-instance');

			if (instance) {
				instance.reset();
			}
		}
	};

	$(document).ready(function documentReady() {
		$('.mh-dragdrop-container').each(function initMediaHubDragDrop() {
			Adzuki.MediaHubDragDrop.init(this);
		});
	});

}(jQuery, window.Adzuki || {}));
